/**
 * モデル利用割合の円グラフコンポーネント
 *
 * DashboardPage と UserDetailPage で共通使用
 */
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCurrency, formatNumber, tooltipStyle } from './chart-utils';

/** 円グラフの色パレット */
export const PIE_COLORS = [
  '#8b5cf6',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#6366f1',
  '#14b8a6',
];

/** モデル使用量データの型 */
export interface ModelUsageData {
  modelId: string;
  displayName: string;
  cost: number;
  requestCount: number;
}

interface ModelUsagePieChartsProps {
  modelUsage: ModelUsageData[];
}

/**
 * 円グラフのラベルをレンダリング
 */
function renderPieLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  displayName,
  percent,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  displayName: string;
  percent: number;
}) {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#9ca3af"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={10}
    >
      {`${displayName}: ${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

/**
 * 単一の円グラフコンポーネント
 */
export function UsagePieChart({
  title,
  data,
  dataKey,
  tooltipLabel,
  tooltipFormatter,
  cellKeyPrefix,
}: {
  title: string;
  data: ModelUsageData[];
  dataKey: 'cost' | 'requestCount';
  tooltipLabel: string;
  tooltipFormatter: (value: number) => string;
  cellKeyPrefix: string;
}) {
  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <h2 className="text-lg font-semibold text-foreground mb-4">{title}</h2>
      <div className="h-64">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                nameKey="displayName"
                cx="50%"
                cy="50%"
                outerRadius={60}
                label={renderPieLabel}
                labelLine={false}
              >
                {data.map((item, index) => (
                  <Cell
                    key={`${cellKeyPrefix}-${item.modelId}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                {...tooltipStyle}
                formatter={(value: number) => [tooltipFormatter(value), tooltipLabel]}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            データがありません
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * モデル利用割合の円グラフ（金額・回数）
 */
export function ModelUsagePieCharts({ modelUsage }: ModelUsagePieChartsProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <UsagePieChart
        title="モデル利用割合（金額）"
        data={modelUsage}
        dataKey="cost"
        tooltipLabel="金額"
        tooltipFormatter={formatCurrency}
        cellKeyPrefix="cell-cost"
      />
      <UsagePieChart
        title="モデル利用割合（回数）"
        data={modelUsage}
        dataKey="requestCount"
        tooltipLabel="回数"
        tooltipFormatter={formatNumber}
        cellKeyPrefix="cell-count"
      />
    </div>
  );
}
